import { Callout, DefaultEffects, DirectionalHint, getTheme, Stack, Text } from '@fluentui/react';
import { Building2, House, LucideIcon, MapPin } from 'lucide-react';
import React from 'react';
import IvicosStrings from 'kits/language/stringKit';
import { AtWorkLocation } from 'shared-state/identity/types';

export interface IAtWorkOptionsMenuProps {
    isOpen: boolean;
    targetRef: React.RefObject<HTMLElement>;
    selectedLocation: AtWorkLocation;
    onOptionSelect: (location: AtWorkLocation) => void;
    onOpenCustomModal: () => void;
    onClose: () => void;
}

const OPTION_ICON_SIZE = 18;

const AT_WORK_OPTIONS: { key: AtWorkLocation; label: () => string; Icon: LucideIcon }[] = [
    { key: 'office', label: () => IvicosStrings.atWorkOptionOffice, Icon: Building2 },
    { key: 'remote', label: () => IvicosStrings.atWorkOptionRemote, Icon: House },
    { key: 'other_location', label: () => IvicosStrings.atWorkOptionOtherLocation, Icon: MapPin }
];

/**
 * Menu displayed when At work icon is clicked.
 * Shows Office, Remote, Other location options plus custom reason. Only updates on explicit selection.
 */
const AtWorkOptionsMenu: React.FC<IAtWorkOptionsMenuProps> = ({ isOpen, targetRef, onOptionSelect, onOpenCustomModal, onClose }) => {
    const theme = getTheme();

    const handleOptionClick = (location: AtWorkLocation) => {
        onOptionSelect(location);
        onClose();
    };

    const handleCreatePersonalMessage = () => {
        onClose(); // Close menu first so it doesn't dismiss and take the modal with it
        onOpenCustomModal();
    };

    if (!isOpen || !targetRef.current) return null;

    return (
        <Callout
            target={targetRef.current}
            onDismiss={onClose}
            directionalHint={DirectionalHint.bottomCenter}
            isBeakVisible={false}
            style={{
                padding: '12px',
                borderRadius: 8,
                boxShadow: DefaultEffects.elevation16,
                background: theme.palette.white,
                minWidth: 240
            }}
        >
            <Stack tokens={{ childrenGap: 8 }}>
                {AT_WORK_OPTIONS.map((option) => {
                    const Icon = option.Icon;
                    return (
                        <Stack
                            key={option.key}
                            onClick={() => handleOptionClick(option.key)}
                            horizontal
                            verticalAlign="center"
                            tokens={{ childrenGap: 8 }}
                            style={{
                                padding: '8px 12px',
                                borderRadius: 4,
                                cursor: 'pointer',
                                backgroundColor: theme.palette.neutralLighter,
                                transition: 'background-color 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = theme.palette.neutralLight;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = theme.palette.neutralLighter;
                            }}
                        >
                            <Icon size={OPTION_ICON_SIZE} color={theme.palette.neutralPrimary} />
                            <Text style={{ fontSize: 14, color: theme.palette.black, flex: 1 }}>{option.label()}</Text>
                        </Stack>
                    );
                })}

                {/* Divider */}
                <div style={{ height: 1, background: theme.palette.neutralLight, margin: '4px 0' }} />

                {/* Personal message row — pencil matches Away (yellow) menu */}
                <Stack
                    onClick={handleCreatePersonalMessage}
                    style={{
                        padding: '8px 12px',
                        borderRadius: 4,
                        cursor: 'pointer',
                        backgroundColor: theme.palette.neutralLighter,
                        transition: 'background-color 0.15s ease',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 8
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.palette.neutralLight;
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = theme.palette.neutralLighter;
                    }}
                >
                    <Text style={{ fontSize: 14, color: theme.palette.black }}>✏️ {IvicosStrings.atWorkCreatePersonalMessage}</Text>
                </Stack>
            </Stack>
        </Callout>
    );
};

export default AtWorkOptionsMenu;
